import { useState } from "react";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import { PAGE_FORMATS, ORIENTATIONS, getPageDimensions } from "./pageFormats";
import {
  FileText,
  RotateCcw,
  Maximize2,
  Monitor,
} from "lucide-react";

/**
 * PageSettingsDialog - Configure page format, orientation, and dimensions
 */
export function PageSettingsDialog({
  open,
  onOpenChange,
  currentFormat,
  currentOrientation,
  onApply,
}) {
  const [format, setFormat] = useState(currentFormat || "A4");
  const [orientation, setOrientation] = useState(currentOrientation || ORIENTATIONS.PORTRAIT);

  const handleApply = () => {
    const dimensions = getPageDimensions(format, orientation);
    onApply({
      format,
      orientation,
      ...dimensions,
    });
    onOpenChange(false);
  };

  const previewDimensions = getPageDimensions(format, orientation);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-indigo-500" />
            Format de page
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Format selection */}
          <div>
            <Label className="text-sm font-medium text-slate-600 mb-2 block">
              Format
            </Label>
            <div className="grid grid-cols-4 gap-2">
              {Object.values(PAGE_FORMATS).filter(f => f.id !== "CUSTOM").map((f) => (
                <button
                  key={f.id}
                  onClick={() => setFormat(f.id)}
                  className={`py-3 px-2 rounded-lg border-2 text-center transition-all ${
                    format === f.id
                      ? "border-indigo-500 bg-indigo-50 text-indigo-700"
                      : "border-slate-200 hover:border-slate-300 text-slate-600"
                  }`}
                >
                  <span className="font-bold text-lg">{f.label}</span>
                  <span className="block text-xs text-slate-400 mt-1">
                    {f.width}x{f.height}mm
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Orientation */}
          <div>
            <Label className="text-sm font-medium text-slate-600 mb-2 block">
              Orientation
            </Label>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setOrientation(ORIENTATIONS.PORTRAIT)}
                className={`p-4 rounded-lg border-2 flex flex-col items-center gap-2 transition-all ${
                  orientation === ORIENTATIONS.PORTRAIT
                    ? "border-indigo-500 bg-indigo-50"
                    : "border-slate-200 hover:border-slate-300"
                }`}
              >
                <div className="w-8 h-12 border-2 border-current rounded" />
                <span className="font-medium text-sm">Portrait</span>
              </button>
              
              <button
                onClick={() => setOrientation(ORIENTATIONS.LANDSCAPE)}
                className={`p-4 rounded-lg border-2 flex flex-col items-center gap-2 transition-all ${
                  orientation === ORIENTATIONS.LANDSCAPE
                    ? "border-indigo-500 bg-indigo-50"
                    : "border-slate-200 hover:border-slate-300"
                }`}
              >
                <div className="w-12 h-8 border-2 border-current rounded" />
                <span className="font-medium text-sm">Paysage</span>
              </button>
            </div>
          </div>

          {/* Preview */}
          <div className="bg-slate-50 rounded-lg p-4">
            <Label className="text-xs text-slate-500 mb-2 block">Apercu</Label>
            <div className="flex items-center justify-center">
              <div
                className="bg-white border-2 border-slate-300 shadow-sm flex items-center justify-center text-xs text-slate-400"
                style={{
                  width: `${previewDimensions.width / 8}px`,
                  height: `${previewDimensions.height / 8}px`,
                  maxWidth: "150px",
                  maxHeight: "200px",
                }}
              >
                {previewDimensions.widthMm} x {previewDimensions.heightMm} mm
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button onClick={handleApply}>
            Appliquer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * PageFormatIndicator - Shows current page format in toolbar
 */
export function PageFormatIndicator({ format, orientation, onClick }) {
  const fmt = PAGE_FORMATS[format] || PAGE_FORMATS.A4;
  const isLandscape = orientation === ORIENTATIONS.LANDSCAPE;
  
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-2 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm transition-colors"
      title="Modifier le format de page"
    >
      <FileText className="w-4 h-4 text-slate-500" />
      <span className="font-medium text-slate-700">{fmt.label}</span>
      {isLandscape && (
        <RotateCcw className="w-3 h-3 text-slate-400" />
      )}
    </button>
  );
}
